/**
 * Fit a page root to the visible area while the on-screen keyboard is up
 * (#15552, #15564).
 *
 * iOS WebKit keeps the page its full height when the keyboard rises (only
 * `window.visualViewport` shrinks) and scrolls the whole document up to the
 * focused field, which pushes the top of the page off the screen. While a
 * field inside the root has the focus and the visual viewport is shorter than
 * the window, the root ends where the visible area ends, so the field sits on
 * the keyboard and nothing is pushed off the top; a scroll WebKit made to
 * reach the field is undone, and a caret the shrink leaves below the visible
 * bottom is scrolled back into view inside the root, once per shrink.
 *
 * With the focus anywhere else (a field in a portal outside the root, a
 * button, nothing) the root's own height rules and a scroll is left alone: it
 * is the user's, or pull-to-refresh (a negative scroll). A browser with no
 * visual viewport changes nothing, and neither does a pinch zoom, which
 * shrinks the visual viewport with no keyboard up.
 */
import { RefObject, useEffect } from "react";
import { typingSurfaceHasFocus } from "@/utils/typingSurface";

/** How much shorter than the window the visual viewport must be before the
 *  root takes it for a keyboard rather than a rounding difference. */
const KEYBOARD_SLACK_PX = 4;

/** The room kept between a revealed caret and the keyboard's top edge. */
const REVEAL_MARGIN_PX = 8;

/** Where the caret is: for an editable surface the end of its selection, or,
 *  when the selection has no box of its own (a collapsed caret in an empty
 *  paragraph after Enter), the element holding it; else the field itself. */
const caretRect = (field: Element): DOMRect => {
  if (field instanceof HTMLElement && field.isContentEditable) {
    const selection = document.getSelection();
    if (selection && selection.rangeCount > 0) {
      const rects = selection.getRangeAt(0).getClientRects();
      if (rects.length > 0) return rects[rects.length - 1];
      const node = selection.focusNode;
      const holder = node instanceof Element ? node : node?.parentElement;
      if (holder && field.contains(holder)) {
        return holder.getBoundingClientRect();
      }
    }
  }
  return field.getBoundingClientRect();
};

/** A root fitted to the visible area can leave the field it fitted for
 *  below the visible bottom inside the page's own scroller (a caret at the
 *  end of long notes): the topbar shows and the caret does not. Scroll the
 *  nearest scroll region inside the root just far enough to bring the caret
 *  above the keyboard; never the window, never the page behind a fixed
 *  overlay, nothing when it already shows, and nothing when what was
 *  measured is taller than the visible area, where no scroll could show it
 *  whole (#15564). */
const revealFocused = (
  root: HTMLElement,
  field: Element,
  visibleTop: number,
  visibleBottom: number
): void => {
  const caret = caretRect(field);
  if (caret.height > visibleBottom - visibleTop) return;
  const delta = caret.bottom - (visibleBottom - REVEAL_MARGIN_PX);
  if (delta <= 0) return;
  for (
    let node = field.parentElement;
    node !== null && root.contains(node);
    node = node.parentElement
  ) {
    const style = getComputedStyle(node);
    if (
      node.scrollHeight > node.clientHeight &&
      (style.overflowY === "auto" || style.overflowY === "scroll")
    ) {
      node.scrollTop += delta;
      return;
    }
    // A fixed overlay scrolls with nothing behind it.
    if (style.position === "fixed") return;
  }
};

export function useKeyboardFit(
  rootRef: RefObject<HTMLElement | null>,
  enabled: boolean = true
): void {
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = rootRef.current;
    if (!enabled || !viewport || !root) return;
    // The height the root renders with (the app shell's `100dvh`; empty for
    // a root sized by its classes), put back whenever the fit lets go.
    const ownHeight = root.style.height;
    // The height the fit last gave the root, or null while it is unfitted.
    // The caret is revealed only when the root shrinks: a later viewport
    // event at the same height (the suggestion strip, a pan) must not snap
    // a user who scrolled away to reread back to the caret.
    let fittedHeight: number | null = null;
    const unfit = () => {
      root.style.height = ownHeight;
      fittedHeight = null;
    };
    const fitToViewport = () => {
      const active = document.activeElement;
      const typingHere =
        typingSurfaceHasFocus() &&
        active !== null &&
        root.contains(active) &&
        Math.abs(viewport.scale - 1) < 0.01;
      if (!typingHere) {
        unfit();
        return;
      }
      if (window.scrollY > 0) window.scrollTo(0, 0);
      if (window.innerHeight - viewport.height <= KEYBOARD_SLACK_PX) {
        unfit();
        return;
      }
      const visibleTop = viewport.offsetTop;
      const visibleBottom = visibleTop + viewport.height;
      const top = root.getBoundingClientRect().top;
      const height = Math.max(0, visibleBottom - top);
      root.style.height = `${height}px`;
      const shrank = fittedHeight === null || height < fittedHeight;
      fittedHeight = height;
      if (shrank) revealFocused(root, active, visibleTop, visibleBottom);
    };
    viewport.addEventListener("resize", fitToViewport);
    viewport.addEventListener("scroll", fitToViewport);
    // A root that mounts with the keyboard already up fits at once.
    fitToViewport();
    return () => {
      viewport.removeEventListener("resize", fitToViewport);
      viewport.removeEventListener("scroll", fitToViewport);
      root.style.height = ownHeight;
    };
  }, [rootRef, enabled]);
}
